/**
 * SignaturePad — Touch-friendly signature capture for POD.
 *
 * Uses HTML5 Canvas with pointer events (works on touch + mouse).
 * Outputs a PNG data URL via onSign callback.
 */

import { useRef, useState, useEffect, useCallback } from 'react';
import { PenTool, RotateCcw, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface SignaturePadProps {
  /** Called with PNG data URL when signature is confirmed */
  onSign: (dataUrl: string) => void;
  /** Called when user skips signature */
  onSkip?: () => void;
  /** Whether field is disabled */
  disabled?: boolean;
  /** Width of the pad */
  width?: number;
  /** Height of the pad */
  height?: number;
}

export default function SignaturePad({
  onSign,
  onSkip,
  disabled = false,
  width = 320,
  height = 160,
}: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(false);

  // Adjust canvas for device pixel ratio
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(dpr, dpr);
      ctx.strokeStyle = '#1a1a1a';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [width, height]);

  const getPos = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(e.pointerId);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  }, [disabled, getPos]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing || disabled) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasContent(true);
  }, [isDrawing, disabled, getPos]);

  const handlePointerUp = useCallback(() => {
    setIsDrawing(false);
  }, []);

  const handleClear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasContent(false);
  }, []);

  const handleConfirm = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !hasContent) return;
    const dataUrl = canvas.toDataURL('image/png');
    onSign(dataUrl);
  }, [hasContent, onSign]);

  return (
    <div className="space-y-2">
      <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
        <PenTool className="h-3.5 w-3.5" />
        Signature
      </label>

      {/* Canvas area */}
      <div className="rounded-md border bg-white overflow-hidden" style={{ width, maxWidth: '100%' }}>
        <canvas
          ref={canvasRef}
          style={{ width, height, maxWidth: '100%', touchAction: 'none' }}
          className="cursor-crosshair"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        />
      </div>

      {/* Hint */}
      {!hasContent && (
        <p className="text-[10px] text-muted-foreground text-center">
          Sign above with your finger or stylus
        </p>
      )}

      {/* Action buttons */}
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={handleClear}
          disabled={!hasContent || disabled}
          className="flex-1"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Clear
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleConfirm}
          disabled={!hasContent || disabled}
          className="flex-1"
        >
          <Check className="h-3.5 w-3.5 mr-1" /> Confirm
        </Button>
        {onSkip && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onSkip}
            disabled={disabled}
          >
            Skip
          </Button>
        )}
      </div>
    </div>
  );
}
